import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { UsersType } from "../../types/";

type AuthState = {
  users: Record<number, UsersType.UserPartial>; // key is user id
  current: {
    userid: number | null;
    user: UsersType.UserPartial | null;
  };
};

const initial: AuthState = {
  users: {},
  current: {
    userid: null,
    user: null,
  },
};

export const authSlice = createSlice({
  name: "auth",
  initialState: initial as AuthState,
  reducers: {
    addUser(
      state: AuthState,
      action: PayloadAction<{ user: UsersType.UserPartial }>
    ) {
      const { user } = action.payload;
      if (user?.id) {
        state.users[user.id] = user
          ? JSON.parse(JSON.stringify(user))
          : undefined;
      }
    },
    setCurrentUser(
      state: AuthState,
      action: PayloadAction<{ userid: number }>
    ) {
      const { userid } = action.payload;
      if (userid && state.users[userid]) {
        state.current.user = state.users[userid]
          ? JSON.parse(JSON.stringify(state.users[userid]))
          : undefined;
        state.current.userid = userid;
      }
    },
    updateUserData(
      state: AuthState,
      action: PayloadAction<{ userid: number; user: UsersType.UserPartial }>
    ) {
      const { userid, user } = action.payload;
      if (userid && state.users[userid]) {
        state.users[userid] = user
          ? JSON.parse(JSON.stringify(user))
          : undefined;
      }
      if (userid && state.current.userid === userid) {
        state.current.user = user
          ? JSON.parse(JSON.stringify(user))
          : undefined;
      }
    },

    updateCurrentUserData(
      state: AuthState,
      action: PayloadAction<{ user: UsersType.UserPartial }>
    ) {
      const { user } = action.payload;
      if (user?.id && state.current.userid === user.id) {
        state.current.user = user
          ? JSON.parse(JSON.stringify(user))
          : undefined;
      }
    },

    resetUsers(state: AuthState) {
      state.users = {};
    },

    removeUser(state: AuthState, action: PayloadAction<number>) {
      const userid = action.payload;
      delete state.users[userid];

      if (state.current.userid === userid) {
        state.current.userid = null;
        state.current.user = null;
      }
    },

    removeCurrentUser(state: AuthState) {
      const { userid, user } = state.current;
      if (userid && state.users[userid]) {
        delete state.users[userid];
      }
      state.current.user = null;
      state.current.userid = null;
    },

    resetAllUsers(state: AuthState) {
      state.users = {};
      state.current.user = null;
      state.current.userid = null;
    },
  },
});

export const {
  addUser,
  setCurrentUser,
  updateUserData,
  updateCurrentUserData,
  resetUsers,
  removeUser,
  resetAllUsers,
  removeCurrentUser,
} = authSlice.actions;
export const authReducer = authSlice.reducer;
